import { useMemo } from 'react'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import {
  createFilterColumnHelper,
  DataTypeColumn,
} from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { FilterIdentifier } from 'models/saved_view'
import useApiService from 'services/api'

type DealRemindersFilterColumnKey =
  | 'scheduled_after'
  | 'reminder_type'
  | 'created_by'
  | 'recipient'

const DEAL_REMINDERS_FILTER_COLUMNS: Record<
  DealRemindersFilterColumnKey,
  DataTypeColumn
> = {
  scheduled_after: { header: 'Scheduled Date', group: 'Dates' },
  reminder_type: { header: 'Frequency', group: 'Reminder Details' },
  created_by: { header: 'Created By', group: 'People' },
  recipient: { header: 'Recipients', group: 'People' },
}

const REMINDER_TYPE_OPTIONS: SelectFilterOption[] = [
  { label: 'Once', value: 'one_off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Annually', value: 'annually' },
  { label: 'Custom', value: 'custom' },
]

interface DealRemindersFiltersetProps {
  filterIdentifier: FilterIdentifier
}

/** Filter modal for the deal reminders tab with date, frequency, and people filters. */
export default function DealRemindersFilterset({
  filterIdentifier,
}: DealRemindersFiltersetProps) {
  const api = useApiService()
  const { data: rawUserOptions } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: true,
  })

  const userOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawUserOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawUserOptions])

  const filterStore = filterStoreRepo.getStore(filterIdentifier)
  const filterHelper = createFilterColumnHelper({
    columns: DEAL_REMINDERS_FILTER_COLUMNS,
  })

  return (
    <FilterModal store={filterStore} identifier={filterIdentifier}>
      <FilterGroup title="Date Filters">
        <DateRangeFilter
          {...filterHelper.retrieveProps('scheduled_after')}
          filterStore={filterStore}
        />
      </FilterGroup>
      <FilterGroup title="Reminder Details">
        <SelectFilter
          {...filterHelper.retrieveProps('reminder_type')}
          options={REMINDER_TYPE_OPTIONS}
          filterStore={filterStore}
        />
      </FilterGroup>
      <FilterGroup title="People">
        <SelectFilter
          {...filterHelper.retrieveProps('created_by')}
          options={userOptions || []}
          filterStore={filterStore}
        />
        <SelectFilter
          {...filterHelper.retrieveProps('recipient')}
          options={userOptions || []}
          filterStore={filterStore}
        />
      </FilterGroup>
    </FilterModal>
  )
}
